// Takes survive a page refresh: they're kept in IndexedDB on this device.
// Every call is best-effort; private windows may refuse storage entirely.
import type { StereoAudio } from './audio-data';

export interface Take {
  id: string;
  name: string;
  presetName: string;
  createdAt: number;
  audio: StereoAudio;
  video?: { blob: Blob; type: string; ext: string };
}

const DB = 'spiel';
const STORE = 'takes';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function loadTakes(): Promise<Take[]> {
  try {
    const all = await run<Take[]>('readonly', (s) => s.getAll());
    return all.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

export async function saveTake(take: Take): Promise<boolean> {
  try {
    await run('readwrite', (s) => s.put(take));
    return true;
  } catch {
    return false;
  }
}

export async function removeTake(id: string): Promise<void> {
  try {
    await run('readwrite', (s) => s.delete(id));
  } catch {
    /* nothing to clean up */
  }
}
